/**
 * 기업 정보 — 보고 고치는 한 벌
 * ---------------------------------------------------------------------------
 * 같은 내용을 두 곳에서 쓴다.
 *
 *   운영자   시스템 관리 > 고객 > 기업 관리 → 줄을 누르면 뜨는 상세 창
 *   기업     설정 > 기업 설정 → 「기업 정보」 탭
 *
 * 그래서 화면을 한 벌만 만들고 **부르는 주소만 바꿔 쓴다.** 두 벌로 만들면
 * 칸을 하나 늘릴 때 한쪽에만 늘어나고, "내 화면에서는 고쳐지는데 운영자
 * 화면에서는 안 된다" 가 된다.
 *
 * 서류 첨부도 같은 이유로 여기 함께 둔다. 사업자등록증은 기업 정보의
 * 일부지 별개의 기능이 아니다.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { useCodes } from '../../lib/codes';

const { Text, Paragraph } = Typography;

export interface TenantProfile {
  tenantId: string;
  slug: string;
  companyName: string;
  companyNameEn: string | null;
  bizRegNo: string | null;
  corpRegNo: string | null;
  bizType: string | null;
  bizItem: string | null;
  zipCode: string | null;
  address1: string | null;
  address2: string | null;
  companyTel: string | null;
  companyFax: string | null;
  establishedOn: string | null;
  homepageUrl: string | null;
  ceoName: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  status: string;
  memberCount: number;
}

interface TenantDocument {
  documentId: string;
  docType: string;
  docTypeName: string | null;
  memo: string | null;
  fileId: string;
  fileName: string;
  byteSize: number;
  status: string;
  createdAt: string;
  createdByName: string | null;
}

interface UploadTicket {
  fileId: string;
  uploadUrl: string;
  method?: string;
  headers?: Record<string, string>;
}

/** 첨부 결과. 한 장만 두는 종류를 바꿔치기했으면 무엇을 치웠는지 들어 있다. */
interface AttachResult {
  replaced: Array<{ documentId: string; fileName: string }>;
}

/** 사업자등록증에서 읽어 온 값. 서버가 칸 이름을 폼과 같게 맞춰 준다. */
interface ScanResult {
  fields: {
    companyName: string | null;
    bizRegNo: string | null;
    corpRegNo: string | null;
    ceoName: string | null;
    establishedOn: string | null;
    bizType: string | null;
    bizItem: string | null;
    address1: string | null;
  };
  /** 읽지 못한 칸 이름. 사람이 직접 적어야 하는 것들 */
  unread: string[];
  model: string;
}

/** 읽은 값을 보여 줄 때 쓰는 이름. 폼의 라벨과 같아야 찾기 쉽다. */
const SCAN_LABELS: Record<keyof ScanResult['fields'], string> = {
  companyName: '상호',
  bizRegNo: '사업자등록번호',
  corpRegNo: '법인등록번호',
  ceoName: '대표자',
  establishedOn: '개업연월일',
  bizType: '업태',
  bizItem: '종목',
  address1: '사업장 주소',
};

interface Props {
  /**
   * 기업 정보를 읽고 쓰는 주소.
   *
   *   기업 자신   `/tenant`
   *   운영자      `/admin/tenants/{id}/profile`
   *
   * 둘이 다른 모양이라 서류 주소와 따로 받는다. 하나로 합쳐 뒤에 `/profile`
   * 을 붙이면 기업 쪽이 `/tenant/profile` 이 되어 없는 경로가 된다.
   */
  profilePath: string;
  /**
   * 서류 주소.
   *
   *   기업 자신   `/tenant/documents`
   *   운영자      `/admin/tenants/{id}/documents`
   */
  docsPath: string;
  /** 캐시를 구분할 열쇠. 기업마다 따로 담아 두어야 한다 */
  cacheKey: string;
  /** 고칠 수 있는가. 권한이 없으면 읽기만 */
  editable?: boolean;
}

const prettyBytes = (n: number) =>
  n >= 1024 ** 2 ? `${(n / 1024 ** 2).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

export function TenantProfileForm({ profilePath, docsPath, cacheKey, editable = true }: Props) {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [form] = Form.useForm();
  const codes = useCodes(['TENANT_DOC_TYPE']);

  const [docType, setDocType] = useState('BIZ_LICENSE');
  const [docMemo, setDocMemo] = useState('');
  const [uploading, setUploading] = useState(false);
  /** 지금 읽고 있는 서류. 단추를 잠그고 돌아가는 중임을 보인다 */
  const [scanning, setScanning] = useState<string | null>(null);
  /** 읽어 온 값. 사람이 확인하고 「채우기」를 눌러야 폼에 들어간다 */
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);

  const profileKey = ['tenant-profile', cacheKey];
  const docKey = ['tenant-documents', cacheKey];

  /**
   * 사업자등록증을 올리거나 지우면 **자사 현황의 안내 띠**도 달라진다.
   *
   * 그 값(`['tenant','onboarding']`)은 5분 동안 담겨 있고 창을 옮겨도 다시
   * 묻지 않는다(`refetchOnWindowFocus: false`). 버려 주지 않으면 **방금
   * 올렸는데 「사업자등록증이 등록되지 않았습니다」가 그대로 떠 있다.**
   *
   * 운영자가 남의 기업을 고치는 경우에는 그 운영자의 안내 띠와 아무 상관이
   * 없지만, 버려서 손해 볼 것도 없다(다음에 한 번 더 물을 뿐이다).
   */
  const refreshOnboarding = () => client.invalidateQueries({ queryKey: ['tenant', 'onboarding'] });

  const profile = useQuery<TenantProfile, NormalizedError>({
    queryKey: profileKey,
    queryFn: async () => (await apiClient.get<TenantProfile>(profilePath)).data,
  });

  const documents = useQuery<TenantDocument[], NormalizedError>({
    queryKey: docKey,
    queryFn: async () =>
      (await apiClient.get<TenantDocument[]>(docsPath)).data,
  });

  useEffect(() => {
    if (profile.data) form.setFieldsValue(profile.data);
  }, [profile.data, form]);

  /*
   * 한 장만 두는 종류인가. **화면에 종류 이름을 박지 않는다** — 공통코드
   * TENANT_DOC_TYPE 의 값이 'SINGLE' 인 것만 그렇게 본다. 서버도 같은
   * 공통코드를 본다. 「인감증명서도 한 장만」 같은 결정이 나면 배포 없이
   * 공통코드 화면에서 바꾸면 양쪽이 같이 바뀐다.
   */
  const docTypeLabel = codes.label('TENANT_DOC_TYPE', docType);
  const existingOfType = (documents.data ?? []).filter((d) => d.docType === docType);
  const replacesExisting =
    codes.value('TENANT_DOC_TYPE', docType) === 'SINGLE' && existingOfType.length > 0;

  const save = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      /*
       * 빈 문자열은 null 로 바꿔 보낸다. 서버 스키마가 url·email 형식을
       * 검사하므로 빈 문자열을 그대로 보내면 "형식이 올바르지 않습니다" 가
       * 된다 — 지우려고 비운 것인데 저장이 안 된다.
       */
      const payload: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(values)) {
        if (v === undefined) continue;
        payload[k] = typeof v === 'string' && v.trim() === '' ? null : v;
      }
      // 고칠 수 없는 값은 보내지 않는다.
      delete payload.tenantId;
      delete payload.slug;
      delete payload.status;
      delete payload.memberCount;
      delete payload.companyName_disabled;

      await apiClient.patch(profilePath, payload);
    },
    onSuccess: async () => {
      message.success('기업 정보를 저장했습니다.');
      await client.invalidateQueries({ queryKey: profileKey });
    },
    onError: (err: NormalizedError) => message.error(errorText(err)),
  });

  /**
   * 서류 올리기.
   *
   * 저장소가 주는 전송 주소로 **브라우저가 직접** 보낸다. API 서버를 거치지
   * 않으므로 큰 파일에도 API 가 막히지 않는다. 보낸 뒤 확정을 불러야 서류로
   * 등록된다 — 확정 전에 멈추면 저장소에만 남고 목록에는 안 보인다.
   */
  const upload = async (file: File): Promise<void> => {
    /*
     * 한 장만 두는 종류인데 이미 있으면 **먼저 묻는다.**
     *
     * 서버도 묻지 않은 바꿔치기를 거절하지만(409), 거절당한 뒤에 묻는
     * 순서로 만들면 그때는 이미 파일이 저장소에 올라가 있다. 올리기 전에
     * 묻는 편이 낫다 — 큰 파일을 보내 놓고 "역시 하지 마세요" 가 되지
     * 않는다.
     */
    if (replacesExisting) {
      const confirmed = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: `이미 ${docTypeLabel}이(가) 첨부되어 있습니다`,
          content: (
            <Space direction="vertical" size={4}>
              <Text>
                이 종류는 <b>한 장만 보관</b>합니다. 새로 올리시면{' '}
                <b>기존 파일을 삭제하고 이 파일로 대체</b>합니다.
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                지워질 파일: {existingOfType.map((d) => d.fileName).join(' · ')}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                삭제된 파일은 휴지통으로 가며, 기록은 감사로그에 남습니다.
              </Text>
            </Space>
          ),
          okText: '대체합니다',
          okButtonProps: { danger: true },
          cancelText: '그만두기',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;
    }

    setUploading(true);
    try {
      const { data: ticket } = await apiClient.post<UploadTicket>(
        `${docsPath}/upload-url`,
        { fileName: file.name, contentType: file.type || 'application/octet-stream', byteSize: file.size },
      );

      const res = await fetch(ticket.uploadUrl, {
        method: ticket.method ?? 'PUT',
        headers: ticket.headers ?? { 'content-type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!res.ok) throw new Error(`저장소가 파일을 받지 못했습니다 (HTTP ${res.status})`);

      const { data: attached } = await apiClient.post<AttachResult>(docsPath, {
        fileId: ticket.fileId,
        docType,
        memo: docMemo.trim() || null,
        replaceExisting: replacesExisting,
      });

      setDocMemo('');
      // 무엇이 지워졌는지까지 말한다. 「대체했습니다」만으로는 확인할 길이 없다.
      const replaced = attached?.replaced ?? [];
      message.success(
        replaced.length > 0
          ? `${replaced.map((r) => r.fileName).join(' · ')} 을(를) 삭제하고 ${file.name} 으로 대체했습니다.`
          : `${file.name} 을 첨부했습니다.`,
      );
      await client.invalidateQueries({ queryKey: docKey });
      await refreshOnboarding();

      /*
       * 사업자등록증이면 바로 읽어 본다.
       *
       * 사람이 「자동 읽기」 단추를 따로 찾게 하지 않는다 — 방금 올린
       * 그 서류에 적힌 값을 바로 아래 칸에 그대로 다시 적는 것이 지금
       * 하는 일이기 때문이다.
       *
       * 실패해도 올리기는 성공이다. 읽기가 안 됐다고 첨부를 되돌리면
       * 서류가 필요해서 올린 사람에게 아무것도 남지 않는다.
       */
      if (docType === 'BIZ_LICENSE') {
        const docs = await apiClient
          .get<TenantDocument[]>(docsPath)
          .then((r) => r.data)
          .catch(() => [] as TenantDocument[]);
        const just = docs.find((d) => d.fileId === ticket.fileId);
        if (just) await scan(just, { silentWhenUnavailable: true });
      }
    } catch (err) {
      const normalized = err as NormalizedError;
      message.error(normalized.message ? errorText(normalized) : (err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  /**
   * 사업자등록증을 AI 로 읽어 칸을 채운다.
   *
   * **바로 저장하지 않는다.** 읽은 값을 보여 주고, 「채우기」를 눌러야
   * 폼에 들어가며, 저장은 사람이 따로 누른다. OCR 은 틀린다 — 특히
   * 「주식회사」와 「(주)」, 숫자 0 과 O 가 그렇다. 사업자등록번호가 한
   * 자리 틀리면 **세금계산서가 거절당하거나 엉뚱한 사업자에게 발행된다.**
   */
  const scan = async (
    row: TenantDocument,
    opts: { silentWhenUnavailable?: boolean } = {},
  ): Promise<void> => {
    setScanning(row.documentId);
    try {
      const { data } = await apiClient.post<ScanResult>(`${docsPath}/${row.documentId}/scan`);
      setScanResult(data);
    } catch (err) {
      const normalized = err as NormalizedError;
      // AI 가 꺼져 있는 것은 사람의 잘못이 아니다. 올린 직후라면 조용히 넘긴다.
      if (opts.silentWhenUnavailable && normalized.status === 503) return;
      message.error(errorText(normalized));
    } finally {
      setScanning(null);
    }
  };

  /**
   * 읽은 값을 폼에 넣는다.
   *
   * **이미 적혀 있던 값 위에 덮는다.** 주소가 바뀌어 새 등록증을 올린
   * 경우가 바로 이 기능을 쓰는 자리이기 때문이다 — 옛 주소를 남겨 두면
   * 읽어 온 보람이 없다. 대신 무엇으로 덮는지는 「채우기」를 누르기 전에
   * 창에서 전부 보여 준다.
   *
   * **읽지 못한 칸(null)은 건드리지 않는다.** 못 읽었다는 이유로 멀쩡히
   * 적혀 있던 값을 지워 버리면 안 된다.
   */
  const applyScan = (): void => {
    if (!scanResult) return;
    const filled = Object.entries(scanResult.fields).filter(([, v]) => v !== null);
    form.setFieldsValue(Object.fromEntries(filled));
    setScanResult(null);
    message.success(
      `${filled.length}개 칸을 채웠습니다. 확인하신 뒤 아래 「저장」을 눌러 주세요.`,
    );
  };

  const download = async (row: TenantDocument) => {
    try {
      const { data } = await apiClient.post<{ url: string }>(
        `${docsPath}/${row.documentId}/download-url`,
      );
      window.open(data.url, '_blank', 'noopener');
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  const removeDoc = async (row: TenantDocument) => {
    try {
      await apiClient.delete(`${docsPath}/${row.documentId}`);
      message.success('서류를 삭제했습니다.');
      await client.invalidateQueries({ queryKey: docKey });
      await refreshOnboarding();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  const docTypeOptions = codes.options('TENANT_DOC_TYPE');

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {profile.isError && <Alert type="error" showIcon message={errorText(profile.error)} />}

      <Form form={form} layout="vertical" requiredMark={false} disabled={!editable}>
        <Space size="middle" wrap style={{ display: 'flex' }} align="start">
          <Form.Item
            name="companyName"
            label="상호"
            rules={[{ required: true, message: '상호를 입력하세요' }]}
            style={{ width: 260 }}
          >
            <Input />
          </Form.Item>
          <Form.Item name="companyNameEn" label="영문 상호" style={{ width: 240 }}>
            <Input placeholder="WORK&IT Co., Ltd." />
          </Form.Item>
          <Form.Item
            name="bizRegNo"
            label="사업자등록번호"
            extra="숫자 10자리, 하이픈 없이"
            style={{ width: 180 }}
          >
            <Input placeholder="1234567890" maxLength={10} />
          </Form.Item>
          <Form.Item
            name="corpRegNo"
            label="법인등록번호"
            extra="숫자 13자리, 하이픈 없이"
            style={{ width: 180 }}
          >
            <Input placeholder="1101111234567" maxLength={13} />
          </Form.Item>
          <Form.Item name="ceoName" label="대표자" style={{ width: 150 }}>
            <Input />
          </Form.Item>
        </Space>

        <Space size="middle" wrap style={{ display: 'flex' }} align="start">
          <Form.Item name="bizType" label="업태" style={{ width: 200 }}>
            <Input placeholder="서비스업" />
          </Form.Item>
          <Form.Item name="bizItem" label="종목" style={{ width: 200 }}>
            <Input placeholder="소프트웨어 개발" />
          </Form.Item>
          <Form.Item name="companyTel" label="대표 전화" style={{ width: 170 }}>
            <Input placeholder="02-0000-0000" />
          </Form.Item>
          <Form.Item name="companyFax" label="팩스" style={{ width: 170 }}>
            <Input placeholder="02-0000-0001" />
          </Form.Item>
          <Form.Item
            name="establishedOn"
            label="설립일"
            extra="YYYY-MM-DD"
            style={{ width: 150 }}
          >
            <Input placeholder="2015-03-02" maxLength={10} />
          </Form.Item>
          <Form.Item
            name="homepageUrl"
            label="홈페이지"
            extra="https:// 를 포함해 주세요"
            style={{ width: 260 }}
          >
            <Input placeholder="https://example.co.kr" />
          </Form.Item>
        </Space>

        <Space size="middle" wrap style={{ display: 'flex' }} align="start">
          <Form.Item name="zipCode" label="우편번호" style={{ width: 120 }}>
            <Input maxLength={10} />
          </Form.Item>
          <Form.Item name="address1" label="주소" style={{ width: 380 }}>
            <Input />
          </Form.Item>
          <Form.Item name="address2" label="상세 주소" style={{ width: 260 }}>
            <Input />
          </Form.Item>
        </Space>

        <Space size="middle" wrap style={{ display: 'flex' }} align="start">
          <Form.Item name="contactName" label="담당자" style={{ width: 150 }}>
            <Input />
          </Form.Item>
          <Form.Item name="contactEmail" label="담당자 메일" style={{ width: 260 }}>
            <Input placeholder="contact@example.co.kr" />
          </Form.Item>
          <Form.Item name="contactPhone" label="담당자 연락처" style={{ width: 180 }}>
            <Input placeholder="010-0000-0000" />
          </Form.Item>
        </Space>

        {editable && (
          <Button
            type="primary"
            loading={save.isPending}
            onClick={() => {
              void form.validateFields().then((v) => save.mutate(v));
            }}
          >
            기업 정보 저장
          </Button>
        )}
      </Form>

      {/* ───────────────────────── 서류 ───────────────────────── */}
      <div>
        <Typography.Title level={5} style={{ marginBottom: 4 }}>
          첨부 서류
        </Typography.Title>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          사업자등록증·통장사본처럼 기업에 딸린 서류를 보관합니다.{' '}
          <b>사업자등록증 · 통장 사본 · 인감증명서는 한 장만 보관합니다</b> — 새로 올리시면
          기존 파일을 지우고 새 파일로 대체하며, 지우기 전에 한 번 여쭤 봅니다. 계약서 같은
          나머지 종류는 여러 장을 함께 두실 수 있습니다.
        </Paragraph>

        {/*
          고르자마자 알려 준다. 파일을 고른 뒤에야 "사실 지워집니다" 라고 하면,
          이미 마음먹고 누른 사람에게 묻는 꼴이 된다.
        */}
        {replacesExisting && editable && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`이미 ${docTypeLabel}이(가) 첨부되어 있습니다`}
            description={
              <>
                새로 올리시면 <b>{existingOfType.map((d) => d.fileName).join(' · ')}</b> 을(를)
                지우고 새 파일로 대체합니다.
              </>
            }
          />
        )}

        {editable && (
          <Space wrap align="start" style={{ marginBottom: 12 }}>
            <Select
              value={docType}
              onChange={setDocType}
              style={{ width: 170 }}
              options={docTypeOptions}
            />
            <Input
              value={docMemo}
              onChange={(e) => setDocMemo(e.target.value)}
              placeholder="메모 (발급일·유효기간 등)"
              maxLength={300}
              style={{ width: 260 }}
            />
            <Upload
              showUploadList={false}
              fileList={[] as UploadFile[]}
              beforeUpload={(file) => {
                void upload(file as unknown as File);
                // 우리가 직접 보낸다. antd 의 기본 전송은 쓰지 않는다.
                return false;
              }}
            >
              <Button icon={<UploadOutlined />} loading={uploading}>
                파일 선택
              </Button>
            </Upload>
          </Space>
        )}

        <Table<TenantDocument>
          rowKey="documentId"
          size="small"
          pagination={false}
          loading={documents.isLoading}
          dataSource={documents.data ?? []}
          locale={{ emptyText: '첨부된 서류가 없습니다.' }}
          columns={[
            {
              title: '종류',
              dataIndex: 'docTypeName',
              width: 130,
              render: (v: string | null, row) => <Tag>{v ?? row.docType}</Tag>,
            },
            { title: '파일명', dataIndex: 'fileName', ellipsis: true },
            {
              title: '크기',
              dataIndex: 'byteSize',
              width: 90,
              align: 'right',
              render: (v: number) => prettyBytes(v),
            },
            {
              title: '메모',
              dataIndex: 'memo',
              ellipsis: true,
              render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
            },
            {
              title: '올린 사람',
              dataIndex: 'createdByName',
              width: 110,
              render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
            },
            {
              title: '올린 날짜',
              dataIndex: 'createdAt',
              width: 150,
              render: (v: string) => new Date(v).toLocaleString('ko-KR'),
            },
            {
              title: '',
              width: 160,
              render: (_, row) => (
                <Space size={0}>
                  {/*
                    사업자등록증에만 붙인다. 통장 사본·계약서에서 기업 정보를
                    읽을 일은 없고, 단추만 늘면 무엇에 쓰는지 흐려진다.
                  */}
                  {editable && row.docType === 'BIZ_LICENSE' && (
                    <Button
                      type="link"
                      size="small"
                      loading={scanning === row.documentId}
                      onClick={() => void scan(row)}
                    >
                      자동 읽기
                    </Button>
                  )}
                  <Button
                    type="text"
                    icon={<DownloadOutlined />}
                    onClick={() => void download(row)}
                    aria-label="내려받기"
                  />
                  {editable && (
                    <Popconfirm
                      title="이 서류를 삭제할까요?"
                      description="파일도 함께 휴지통으로 갑니다."
                      okText="삭제"
                      cancelText="취소"
                      onConfirm={() => void removeDoc(row)}
                    >
                      <Button type="text" danger icon={<DeleteOutlined />} aria-label="삭제" />
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </div>

      {/*
        읽어 온 값 확인.

        **바로 채우지 않는다.** 무엇을 읽었는지 보여 주고 사람이 「채우기」를
        눌러야 폼에 들어가며, 저장은 또 한 번 눌러야 한다. OCR 은 틀린다 —
        특히 「주식회사」와 「(주)」, 숫자 0 과 O 가 그렇다. 사업자등록번호가
        한 자리 틀리면 세금계산서가 거절당하거나 엉뚱한 사업자에게 발행된다.
      */}
      <Modal
        open={scanResult !== null}
        title="사업자등록증에서 읽은 값"
        okText="칸에 채우기"
        cancelText="쓰지 않기"
        onCancel={() => setScanResult(null)}
        onOk={applyScan}
        width={560}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="확인하고 저장해 주세요"
          description={
            <>
              자동으로 읽은 값이라 <b>틀릴 수 있습니다.</b> 특히{' '}
              <b>사업자등록번호와 상호</b>는 서류와 한 글자씩 맞춰 보신 뒤 저장해 주세요 —
              틀리면 세금계산서가 발행되지 않습니다. 「채우기」를 눌러도 아직 저장되지
              않습니다.
              <br />
              아래 값은 <b>이미 적혀 있던 값 위에 덮어씁니다.</b> 읽지 못한 칸은 그대로
              둡니다.
            </>
          }
        />
        <Descriptions size="small" column={1} bordered>
          {(Object.keys(SCAN_LABELS) as Array<keyof ScanResult['fields']>).map((key) => (
            <Descriptions.Item key={key} label={SCAN_LABELS[key]}>
              {scanResult?.fields[key] ?? (
                <Text type="secondary">읽지 못했습니다 — 직접 입력해 주세요</Text>
              )}
            </Descriptions.Item>
          ))}
        </Descriptions>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          읽은 모델: {scanResult?.model}
          {/*
            사업자등록번호는 검증번호가 맞지 않으면 서버가 비워서 돌려준다.
            왜 비었는지 말해 주지 않으면 "AI 가 못 읽었나" 로만 읽힌다.
          */}
          {scanResult && scanResult.fields.bizRegNo === null && (
            <>
              <br />
              사업자등록번호는 <b>검증번호가 맞지 않아</b> 비워 두었습니다. 잘못 읽었을
              가능성이 큽니다 — 서류를 보고 직접 입력해 주세요.
            </>
          )}
        </Text>
      </Modal>
    </Space>
  );
}
